import { create } from 'zustand';
import { clientsApi } from '../services/api';
import { Client, ClientDetailData, ClientListItem } from '../types';

/**
 * Cache for the Practice client book (mirrors mattersStore): list + per-client
 * detail (contact info + their cases) render instantly from cache while a
 * refresh runs in the background.
 */
interface ClientsState {
  clients: ClientListItem[];
  detailById: Record<string, ClientDetailData>;
  lastFetched: number | null;
  isFetching: boolean;
  fetchClients: () => Promise<void>;
  /** Create a client and prepend it to the cache; returns the new client. */
  createClient: (data: {
    name: string;
    phone?: string;
    whatsapp?: string;
    email?: string;
    address?: string;
    notes?: string;
  }) => Promise<Client>;
  /** Cache a detail payload and sync the list entry with it. */
  setClientDetail: (detail: ClientDetailData) => void;
  /** Drop a client from both caches (after delete). */
  clearClient: (id: string) => void;
}

export const useClientsStore = create<ClientsState>((set, get) => ({
  clients: [],
  detailById: {},
  lastFetched: null,
  isFetching: false,
  fetchClients: async () => {
    if (get().isFetching) return;
    set({ isFetching: true });
    try {
      const { data } = await clientsApi.list();
      set({ clients: data, lastFetched: Date.now(), isFetching: false });
    } catch {
      set({ isFetching: false });
    }
  },
  createClient: async (data) => {
    const { data: client } = await clientsApi.create(data);
    set((s) => ({ clients: [{ ...client, cases_count: 0 }, ...s.clients] }));
    return client;
  },
  setClientDetail: (detail) =>
    set((s) => ({
      detailById: { ...s.detailById, [detail.id]: detail },
      clients: s.clients.map((c) =>
        c.id === detail.id
          ? {
              ...c,
              name: detail.name,
              phone: detail.phone,
              whatsapp: detail.whatsapp,
              email: detail.email,
              address: detail.address,
              notes: detail.notes,
              updated_at: detail.updated_at,
              cases_count: detail.cases.length,
            }
          : c
      ),
    })),
  clearClient: (id) =>
    set((s) => {
      const { [id]: _dropped, ...rest } = s.detailById;
      return { detailById: rest, clients: s.clients.filter((c) => c.id !== id) };
    }),
}));
